// src/components/js/ConsultaSAC.js

import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ptBR } from 'date-fns/locale';
import { useLocation } from 'react-router-dom';
import { format, parseISO } from 'date-fns';

import SacService from '../../modules/services/SacService';
import { GoChevronDown, GoChevronUp } from "react-icons/go";
import '../css/boletos.css';
import logo from '../images/logo_fundo_branco.png';

function ConsultaSAC({ cliente }) {
    const [filtros, setFiltros] = useState({
        cdOcorrencia: '',
        nrNfe: '',
        dtRegistroInicial: null,
        dtRegistroFinal: null,
        dtEmissaoInicial: null,
        dtEmissaoFinal: null
    });
    const [isLoading, setIsLoading] = useState(false);
    const [erro, setErro] = useState('');
    const [ocorrencias, setOcorrencias] = useState([]);
    const [expandedRows, setExpandedRows] = useState([]);

    const location = useLocation();

    useEffect(() => {
        // Verifica se existe uma nota vinda do redirecionamento do RegistroSAC
        if (location.state?.nrNfe) {
            const nrNfeRecebida = location.state.nrNfe;
            
            // Atualiza o filtro na tela
            setFiltros(prev => ({ ...prev, nrNfe: nrNfeRecebida }));
    
            // 2. Cria a função de busca imediata para não depender do estado filtros que é assíncrono
            const buscarEExpandir = async () => {
                setIsLoading(true);
                try {
                    // Filtros para a busca imediata
                    const filtrosBusca = { nrNfe: nrNfeRecebida };
                    const response = await SacService.consultarOcorrencias(cliente.cdCliente, filtrosBusca);
                    
                    if (response.status === 'OK' && response.conteudo) {
                        const dados = Array.isArray(response.conteudo) ? response.conteudo : [response.conteudo];
                        setOcorrencias(dados);
                        
                        // 3. SE ENCONTRAR RESULTADO, ABRE OS DETALHES AUTOMATICAMENTE
                        if (dados.length > 0) {
                            setExpandedRows([0]); // Abre a primeira linha (índice 0)
                        }
                    } else {
                        setErro(response.mensagem || 'Nenhuma ocorrência encontrada.');
                    }
                } catch (err) {
                    setErro('Erro ao carregar a nova ocorrência.');
                } finally {
                    setIsLoading(false);
                }
            };

            buscarEExpandir();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state]);
    
    // FUNÇÃO AUXILIAR PARA A BUSCA IMEDIATA
    const realizarBuscaDireta = async (filtrosParaUso) => {
        setIsLoading(true);
        try {
            const response = await SacService.consultarOcorrencias(cliente.cdCliente, filtrosParaUso);
            if (response.status === 'OK' && response.conteudo) {
                setOcorrencias(Array.isArray(response.conteudo) ? response.conteudo : [response.conteudo]);
            }
        } catch (err) {
            setErro('Erro ao carregar a nova ocorrência.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFiltros(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (date, fieldName) => {
        setFiltros(prev => ({ ...prev, [fieldName]: date }));
    };

    const handleSearch = async () => {
        setIsLoading(true);
        setErro('');
        setOcorrencias([]);
        try {
          const response = await SacService.consultarOcorrencias(cliente.cdCliente, filtros);
          if (response.status === 'OK' && response.conteudo) {
            // Se a resposta é uma lista (consultar-por-data)
            if (Array.isArray(response.conteudo)) {
              setOcorrencias(response.conteudo);
            } 
            // Se a resposta é um objeto único (consultar)
            else {
              setOcorrencias([response.conteudo]);
            }
          } else {
            setErro(response.mensagem || 'Nenhuma ocorrência encontrada.');
          }
        } catch (err) {
          setErro('Erro ao se comunicar com o servidor.');
        } finally {
          setIsLoading(false);
        }
      };
    
    const toggleRow = (index) => {
        setExpandedRows(prevExpanded => 
            prevExpanded.includes(index) ? prevExpanded.filter(i => i !== index) : [...prevExpanded, index]
        );
    };

    const formatarData = (dataString) => {
        if (!dataString) return 'N/A';
        try {
            const dataObj = parseISO(dataString);
            return format(dataObj, 'dd/MM/yyyy');
        } catch (error) {
            console.error("Erro ao formatar data:", dataString);
            return dataString;
        }
    };

    const formatarSituacao = (situacao) => {
        if (!situacao) return 'Pendente';
        
        // Mapeia o termo técnico para algo amigável
        if (situacao.toUpperCase() === 'FINALIZADO AUTOMATICAMENTE') {
            return 'CONCLUÍDO - DEVOLUÇÃO ACEITA'; // Ou a sugestão que você escolher
        }

        // NOVA REGRA: Altera o nome conforme solicitado pela gestão
        if (situacao.toUpperCase() === 'AGUARDANDO INTEGRAÇÃO') {
            return 'AGUARDANDO GERAÇÃO DO PROTOCOLO';
        }
        
        return situacao;
    };

    return (
        <div className="dashboard-painel boletos-painel">
            <h3 className="titulo">Consulta de Ocorrências SAC</h3>
            <p className="texto">Utilize os filtros abaixo para buscar ocorrências.</p>
            {erro && <p style={{ color: 'red' }}>{erro}</p>}
            
            <div className="boletos-filtros">
                <input
                    type="text"
                    name="cdOcorrencia"
                    placeholder="Nº Ocorrência"
                    value={filtros.cdOcorrencia}
                    onChange={handleInputChange}
                    className="input-filtro"
                />
                <input
                    type="text"
                    name="nrNfe"
                    placeholder="Nº Nota Fiscal"
                    value={filtros.nrNfe}
                    onChange={handleInputChange}
                    className="input-filtro"
                />
                <DatePicker
                    selected={filtros.dtRegistroInicial}
                    onChange={date => handleDateChange(date, 'dtRegistroInicial')}
                    className="input-filtro"
                    placeholderText="Data de Registro Inicial"
                    dateFormat="dd/MM/yyyy"
                    locale={ptBR}
                />
                <DatePicker
                    selected={filtros.dtRegistroFinal}
                    onChange={date => handleDateChange(date, 'dtRegistroFinal')}
                    className="input-filtro"
                    placeholderText="Data de Registro Final"
                    dateFormat="dd/MM/yyyy"
                    locale={ptBR}
                />
                <DatePicker
                    selected={filtros.dtEmissaoInicial}
                    onChange={date => handleDateChange(date, 'dtEmissaoInicial')}
                    className="input-filtro"
                    placeholderText="Data de Emissão Inicial"
                    dateFormat="dd/MM/yyyy"
                    locale={ptBR}
                />
                <DatePicker
                    selected={filtros.dtEmissaoFinal}
                    onChange={date => handleDateChange(date, 'dtEmissaoFinal')}
                    className="input-filtro"
                    placeholderText="Data de Emissão Final"
                    dateFormat="dd/MM/yyyy"
                    locale={ptBR}
                />
                <button className="download-btn" onClick={handleSearch} disabled={isLoading}>
                    {isLoading ? 'Buscando...' : 'Buscar'}
                </button>
            </div>

            <div className="boletos-lista">
                {isLoading ? (
                    <div className="loading-container"><img src={logo} alt="GAM" className="loading-logo" /><div className="spinner"></div><p>Carregando ocorrências...</p></div>
                ) : ocorrencias.length === 0 ? (
                    <p className="mensagem-vazia">Sem ocorrências para os parâmetros informados.</p>
                ) : (
                    <table className="boletos-tabela">
                        <thead>
                            <tr>
                                <th>Protocolo</th>
                                <th>Número da NF</th>
                                <th>Data de Emissão</th>
                                <th>Data de Registro</th>
                                <th>Detalhes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {ocorrencias.map((ocorrencia, index) => (
                                <React.Fragment key={index}>
                                    <tr onClick={() => toggleRow(index)} className={expandedRows.includes(index) ? "expanded" : ""}>
                                        <td>{ocorrencia.cdOcorrencia}</td>
                                        <td>{ocorrencia.itens[0]?.nrNfe}</td>
                                        <td>{formatarData(ocorrencia.itens[0]?.dtEmissao)}</td>
                                        <td>{formatarData(ocorrencia.dtRegistro)}</td>
                                        <td>
                                            <span className="toggle-details">
                                                {expandedRows.includes(index) ? <GoChevronUp /> : <GoChevronDown />}
                                            </span>
                                        </td>
                                    </tr>
                                    {expandedRows.includes(index) && (
                                        <tr className="expanded">
                                            <td colSpan="6">
                                                <div className="expanded-details">
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                <th>Seq.</th>
                                                                <th>Cod. Mercadoria</th>
                                                                <th>Descrição</th>
                                                                <th>Quantidade Devolvida</th>
                                                                <th>Situação</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {ocorrencia.itens.map((item, itemIndex) => (
                                                                <tr key={itemIndex}>
                                                                    <td>{item.seq}</td>
                                                                    <td>{item.cdMercadoria}</td>
                                                                    <td>{item.nmMercadoria}</td>
                                                                    <td>{item.quantidadeMercadoria}</td>
                                                                    <td>{formatarSituacao(item.dsSolucao)}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default ConsultaSAC;